import { Button, Separator, toast } from "@/components/ui";
import { BodyText } from "@/components/ui/Text";
import { TRACKERS } from "@/constants/Trackers";
import { trackEvent } from "@/lib/tracking/tracking";
import { cn } from "@/lib/utils";
import type { RequestGroup, ServiceRequest } from "@curai/js-fhir-tools";
import { LoaderCircle } from "lucide-react";
import type {
  LabOrderEntryActionSummary,
  LabOrderEntryStatusSummary,
} from "../network/useLabOrdersByEncounter";
import { useSubmitOrder } from "../network/useSubmitOrder";
import { useLabOrderUpdateStatus } from "../network/useUpdateLabOrder";
import { AOE } from "./AOE/AOE";
import { OrderForm } from "./OrderForm";
import { TestEntry } from "./TestEntry";
import { TestSearch } from "./TestSearch";

export const OrderEntry = ({
  readOnly,
  patientId,
  encounterId,
  requestGroup,
  serviceRequests,
  statusSummary,
  actionSummary,
}: {
  readOnly: boolean;
  patientId: string;
  encounterId: string;
  requestGroup: RequestGroup;
  serviceRequests: ServiceRequest[];
  statusSummary: LabOrderEntryStatusSummary;
  actionSummary: LabOrderEntryActionSummary;
}) => {
  const { isUpdatingLabOrder, isUpdatingTest, isAddingTest, isRemovingTest } =
    useLabOrderUpdateStatus({
      patientId,
      encounterId,
      requestGroupId: requestGroup.id as string,
    });

  const { mutateAsync: submitOrder, isPending: isSubmittingOrder } =
    useSubmitOrder({
      patientId,
      encounterId,
      requestGroupId: requestGroup.id as string,
      markOnHold: false,
    });

  const isAnythingSaving =
    isUpdatingLabOrder || isUpdatingTest || isAddingTest || isRemovingTest;

  const handleSubmitOrder = async () => {
    if (!actionSummary.readyForSubmission) {
      return;
    }
    trackEvent(TRACKERS.SUBMIT_LAB_ORDER);
    try {
      await submitOrder();
    } catch {
      toast.error("An error occurred submitting the order.");
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <OrderForm
        readOnly={readOnly || isSubmittingOrder}
        patientId={patientId}
        encounterId={encounterId}
        requestGroup={requestGroup}
        serviceRequests={serviceRequests}
        statusSummary={statusSummary}
        actionSummary={actionSummary}
      />
      <Separator />
      {serviceRequests?.length > 0 && (
        <>
          <Separator />
          <div className="flex flex-col gap-2">
            <BodyText size="sm" bold>
              Added Tests
            </BodyText>
            <div
              className={cn(
                "flex flex-col bg-primary p-2 gap-1 rounded-md border-primary border",
                readOnly || isSubmittingOrder
                  ? "bg-tertiary border-secondary"
                  : "bg-primary border-primary",
              )}
            >
              {serviceRequests.map((serviceRequest, index, array) => (
                <>
                  <TestEntry
                    key={`${serviceRequest.id}-${serviceRequest.meta?.versionId}`}
                    serviceRequest={serviceRequest}
                    readOnly={readOnly || isSubmittingOrder}
                    patientId={patientId}
                    encounterId={encounterId}
                    requestGroupId={requestGroup.id as string}
                  />
                  {index < array.length - 1 && <Separator />}
                </>
              ))}
            </div>
          </div>
        </>
      )}
      <TestSearch
        // Intentionally resets to handle blocking duplicates from the search
        key={`${requestGroup.id}-${requestGroup.meta?.versionId}`}
        readOnly={readOnly || isSubmittingOrder}
        patientId={patientId}
        encounterId={encounterId}
        requestGroup={requestGroup}
        actionSummary={actionSummary}
      />
      <div className="flex items-center gap-2 min-h-4">
        {actionSummary.orderHasAoes && (
          <AOE
            patientId={patientId}
            encounterId={encounterId}
            requestGroupId={requestGroup.id as string}
            actionSummary={actionSummary}
            readOnly={readOnly || isSubmittingOrder}
          />
        )}
        <div className="flex flex-row items-center gap-0.5 ml-auto [&>*:last-child]:ml-2">
          {isAnythingSaving && (
            <LoaderCircle className="h-4 w-4 animate-spin stroke-tertiary" />
          )}
          <BodyText size="sm" textColor="tertiary" className="leading-none">
            {isAnythingSaving ? "Saving" : "Saved"}
          </BodyText>
          <Button
            variant="primary"
            size="xs"
            onClick={handleSubmitOrder}
            isLoading={isSubmittingOrder}
            disabled={isAnythingSaving || !actionSummary.readyForSubmission}
            hideTextOnLoading={false}
          >
            {isSubmittingOrder ? "Sending" : "Send to Lab"}
          </Button>
        </div>
      </div>
    </div>
  );
};
